import React, { useState, useEffect } from 'react';
import { useDialogsContext } from '../../../contexts/Dialog';
import { useLinesContext } from '../../../contexts/Lines';
import { formatDateForDb, formatNumber, getStartOfLine } from '../../../utils';
import { DialogNames } from '../../../Types';
import { ILine as Line } from '../../../global';
import { Field, Formik, Form } from 'formik';
import { DatePicker } from 'formik-material-ui-pickers';
import moment from 'moment';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Dialog from '../../BuildingBlocks/Dialogs/DialogStyleOne/Dialog';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import { STOCK_ON_HAND_TEXT_BELOW } from '../messages';

const useStyles = makeStyles((theme) => ({
  field: {
    backgroundColor: 'white',
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1),
    maxWidth: 150,
    '& .MuiInputBase-input': {
      fontSize: 16,
      textAlign: 'center',
      cursor: 'pointer',
    },
    border: 'solid',
    borderWidth: 'thin',
    borderColor: theme.palette.primary.main,
  },
  nextButton: {
    display: 'block',
    fontSize: '20px',
    fontWeight: 700,
    marginTop: theme.spacing(2),
    marginLeft: -theme.spacing(2),
    paddingRight: theme.spacing(3),
    paddingLeft: theme.spacing(3),
  },
  context: {
    display: 'flex',
  },
  contextText: {
    padding: theme.spacing(1),
    marginRight: theme.spacing(2),
  },
}));

interface Props {
  onSubmit: (...args) => void;
  line: Line;
}

export default function StockOnHandDialog({ onSubmit, line }: Props) {
  const classes = useStyles();
  const dialog = useDialogsContext();
  const { products } = useLinesContext();
  const { inventory } = line;
  const [newestProduct, setNewestProduct] = useState(null);
  const lineStart = getStartOfLine(line?.stages);

  const productName =
    newestProduct?.name?.length > 20
      ? newestProduct.name.slice(0, 21) + '...'
      : newestProduct?.name;

  useEffect(() => {
    if (products && products?.length) {
      setNewestProduct(products[products?.length - 1]);
    }
  }, [products, setNewestProduct]);

  const header = (
    <Typography variant="h3">
      When do you expect these{' '}
      <b>
        {inventory ? `${formatNumber(inventory as number)} ` : ''}
        units
      </b>{' '}
      of <b>{productName || 'this product'}</b> to be on hand, ready to sell?
    </Typography>
  );

  const content = (
    <div className={classes.context}>
      <Typography variant="h6" className={classes.contextText}>
        From
      </Typography>
      <Formik
        initialValues={{ date: moment(lineStart).add(65, 'days').toDate() }}
        onSubmit={async (e: any) => {
          dialog.close('stockOnHand');
          onSubmit(formatDateForDb(e.date));
        }}
        component={(values) => {
          return (
            <Form>
              <Field
                component={DatePicker}
                disableToolbar
                autoOk
                InputProps={{
                  disableUnderline: true,
                }}
                disablePast
                name={'date'}
                format="MMM dd yyyy"
                placeholder="mm/dd/yyyy"
                className={classes.field}
              />
              <Button
                className={classes.nextButton}
                color="primary"
                variant="contained"
                type="submit"
                disabled={!values.values.date}
                data-automation-id="submitNext"
              >
                Next
              </Button>
            </Form>
          );
        }}
      />
    </div>
  );

  return (
    <Dialog
      open={dialog[DialogNames.StockOnHand]}
      title={header}
      content={content}
      textBelowActions={STOCK_ON_HAND_TEXT_BELOW}
    />
  );
}
